/**
 * TestCaseRenderer — structured rendering for Athena test cases in the detail panel.
 * Shows test case metadata, steps, variables, assertions, and recent executions.
 * Steps can be merged in as supplement data via testops_get_test_steps.
 */

import { CheckCircle, XCircle, Clock, Tag, Variable, Globe, ListChecks } from 'lucide-react'
import { SupplementSections } from './SupplementSections'

interface TestStep {
  step_number: number
  instruction: string
  step_type: string
  enabled?: boolean
  expected_result?: string
  last_run_status?: string
  last_run_duration_ms?: number
  last_run_error?: string
  data_variables?: string[]
}

interface TestCaseData {
  id?: string
  name?: string
  description?: string
  status?: string
  target_url?: string
  prompt?: string
  step_count?: number
  run_count?: number
  tags?: string[]
  variables?: Record<string, unknown>
  assertions?: unknown[]
  last_run_status?: string
  last_run_at?: string
  created_at?: string
  project_id?: string
  // Merged from testops_get_test_steps
  _steps?: TestStep[]
  // From search results (less detail)
  match_source?: string
  [k: string]: unknown
}

function statusColor(status: string): string {
  const s = status.toLowerCase()
  if (s === 'passed' || s === 'completed') return 'text-success-foreground bg-success-soft border-success/30'
  if (s === 'failed') return 'text-destructive-foreground bg-destructive-soft border-destructive/30'
  if (s === 'running') return 'text-info-foreground bg-info-soft border-info/30'
  if (s === 'active') return 'text-success-foreground bg-success-soft border-success/30'
  if (s === 'draft') return 'text-muted-foreground bg-foreground/[0.04] border-subtle'
  if (s === 'archived') return 'text-subtle-foreground bg-foreground/[0.04] border-subtle'
  return 'text-muted-foreground bg-foreground/[0.04] border-subtle'
}

function stepTypeColor(type: string): string {
  switch (type) {
    case 'action': return 'bg-info-soft text-info-foreground border-info/30'
    case 'assertion': return 'bg-accent-soft text-accent border-accent/30'
    case 'wait': return 'bg-warning-soft text-warning-foreground border-warning/30'
    case 'extract': return 'bg-info-soft text-info-foreground border-info/30'
    case 'conditional': return 'bg-warning-soft text-warning-foreground border-warning/30'
    case 'loop': return 'bg-accent-soft text-accent border-accent/30'
    default: return 'bg-foreground/[0.04] text-muted-foreground border-subtle'
  }
}

/** Renders instruction text with {{variables}} highlighted inline */
function HighlightVariables({ text }: { text: string }) {
  const parts = text.split(/(\{\{[^}]+\}\})/)
  if (parts.length === 1) return <>{text}</>
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith('{{') && part.endsWith('}}') ? (
          <code key={i} className="rounded bg-data-soft border border-data/30 px-1 text-data-foreground font-mono">
            {part}
          </code>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  )
}

function StepStatusIcon({ status }: { status?: string }) {
  if (!status) return null
  if (status === 'passed') return <CheckCircle className="w-3 h-3 text-success" />
  if (status === 'failed') return <XCircle className="w-3 h-3 text-destructive" />
  return <Clock className="w-3 h-3 text-subtle-foreground" />
}

export function TestCaseRenderer({ data }: { data: Record<string, unknown> }) {
  const tc = data as unknown as TestCaseData
  const steps = tc._steps || []

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        {tc.name && (
          <h3 className="font-semibold text-foreground leading-snug">{tc.name}</h3>
        )}
        {tc.description && (
          <p className="text-muted-foreground mt-0.5">{tc.description}</p>
        )}
      </div>

      {/* Status & metadata row */}
      <div className="flex flex-wrap items-center gap-2">
        {tc.status && (
          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 font-semibold ${statusColor(tc.status)}`}>
            {tc.status}
          </span>
        )}
        {tc.last_run_status && (
          <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-medium ${statusColor(tc.last_run_status)}`}>
            <StepStatusIcon status={tc.last_run_status} />
            Last: {tc.last_run_status}
          </span>
        )}
        {tc.run_count != null && tc.run_count > 0 && (
          <span className="inline-flex items-center gap-1 text-muted-foreground">
            {tc.run_count} runs
          </span>
        )}
        {tc.step_count != null && (
          <span className="inline-flex items-center gap-1 text-muted-foreground">
            <ListChecks className="w-3 h-3" /> {tc.step_count} steps
          </span>
        )}
      </div>

      {/* Target URL */}
      {tc.target_url && (
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Globe className="w-3 h-3 shrink-0" />
          <span className="truncate">{tc.target_url}</span>
        </div>
      )}

      {/* Tags */}
      {tc.tags && tc.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          <Tag className="w-3 h-3 text-subtle-foreground mt-0.5" />
          {tc.tags.map((tag) => (
            <span key={tag} className="rounded-full bg-foreground/[0.06] border border-subtle px-2 py-0.5 text-muted-foreground font-medium">
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Variables */}
      {tc.variables && Object.keys(tc.variables).length > 0 && (
        <div>
          <div className="uppercase tracking-wide text-subtle-foreground font-semibold mb-1.5 flex items-center gap-1">
            <Variable className="w-3 h-3" /> Variables
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Object.keys(tc.variables).map((key) => (
              <code key={key} className="rounded bg-data-soft border border-data/30 px-1.5 py-0.5 text-data-foreground font-mono">
                {`{{${key}}}`}
              </code>
            ))}
          </div>
        </div>
      )}

      {/* Steps */}
      {steps.length > 0 && (
        <div>
          <div className="uppercase tracking-wide text-subtle-foreground font-semibold mb-1.5 flex items-center gap-1">
            <ListChecks className="w-3 h-3" /> Steps ({steps.length})
          </div>
          <div className="space-y-1">
            {steps.map((step) => (
              <div
                key={step.step_number}
                className={`flex gap-2 rounded-lg border border-subtle bg-foreground/[0.03] px-2.5 py-1.5 ${
                  step.enabled === false ? 'opacity-40' : ''
                }`}
              >
                <span className="text-subtle-foreground font-mono w-4 shrink-0 text-right">{step.step_number}.</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-1.5">
                    <StepStatusIcon status={step.last_run_status} />
                    <span className="text-foreground">
                      <HighlightVariables text={step.instruction} />
                    </span>
                    {step.step_type !== 'action' && (
                      <span className={`shrink-0 rounded border px-1 py-0.5 font-medium ${stepTypeColor(step.step_type)}`}>
                        {step.step_type}
                      </span>
                    )}
                  </div>
                  {step.last_run_error && (
                    <div className="text-destructive mt-0.5 truncate">{step.last_run_error}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Any other merged supplement sections */}
      <SupplementSections data={data} />
    </div>
  )
}
